/*
  Variant Gallery (Standalone)
  - Listens for `product:variant-change` events
  - Prioritizes window.variantGroups[option1].gallery to update images
  - Restores original markup when no gallery is available
  - Operates independently of existing product.js to avoid side effects
*/

(function () {
  // 启用独立变体图集脚本的全局开关，避免与 product.js 冲突
  window.__variantGalleryStandalone = true;
  const isDesktop = () => window.matchMedia('(min-width: 1024px)').matches;

  const VariantGallery = {
    init(root) {
      this.root = root;
      this.sectionId = this.root.querySelector('variant-selects')?.dataset.section;
      const galleryScope = this.root.closest('.shopify-section') || this.root.parentElement;
      const galleryData = galleryScope?.querySelector('.variant-gallery-data');
      try {
        this.groups = galleryData ? JSON.parse(galleryData.textContent) : [];
      } catch (error) {
        console.warn('VariantGallery: invalid gallery data', error);
        this.groups = [];
      }

      this.lightboxEnabled = !!document.querySelector('.pswp');
      this.grid = this.root.querySelector('.product-gallery-sticky .product-images-grid');
      this.slider = this.root.querySelector('.product-gallery-sticky product-slider');
      this.originalCaptured = false;
      this.initGridPreview();

      // Capture original markup lazily at first variant event to avoid race conditions
      console.log('VariantGallery: init');
      document.addEventListener('product:variant-change', (e) => {
        const { variant, sectionId } = e.detail || {};
        if (this.sectionId && sectionId !== this.sectionId) return;
        console.log('VariantGallery: on variant change', variant);
        this.onVariantChange(variant);
      });
    },

    captureOriginal() {
      if (this.originalCaptured) return;
      if (this.grid) this.originalGridHTML = this.grid.innerHTML;
      if (this.slider) this.originalSliderHTML = this.slider.innerHTML;
      this.originalCaptured = true;
    },

    onVariantChange(variant) {
      if (!variant || !variant.option1) {
        this.restore();
        return;
      }

      const groups = this.groups || [];
      const group = groups.find((g) => g && g.option1 === variant.option1);

      // 如果当前变体在 groups 中没有配置图集，则不要执行任何 DOM 操作，保持当前状态或恢复默认
      // 根据需求：如果没有找到对应的图集，就不要更新轮播（即不要变成空白或者其他变体的图）
      // 但通常来说，如果没有自定义图集，应该显示默认图集？
      // 用户说：“如果没有在window.variantGroups 中找到对应的图集 就不要更新轮播”
      // 这可能意味着如果用户选了一个没有配置图集的变体，应该保留上一个变体的图集？
      // 或者更有可能是指：不要执行 applyGallery，而是执行 restore（显示默认相册）。
      // 但如果理解为字面意思“不要更新”，那就是什么都不做。
      // 不过考虑到代码逻辑，如果什么都不做，页面可能会显示错误的变体图片。
      // 结合之前的上下文“如果simplified 没有数据就显示商品相册”，这里的“不要更新轮播”可能指的是不要尝试用空数据去更新，而是应该回退到默认。
      // 让我们再看一眼之前的代码逻辑：
      /*
      if (group && Array.isArray(group.gallery) && group.gallery.length > 0) {
        ... applyGallery ...
      } else {
        ... restore ...
      }
      */
      // 之前的逻辑是：有图集就用图集，没图集就 restore（回退到默认相册）。
      // 用户现在说：“如果没有...找到对应的图集 就不要更新轮播”。
      // 这可能是在说：手机端现在的 restore 逻辑有问题，导致轮播变空白或者乱了。
      // 如果我直接 return，那么轮播图就会保持在“上一次的状态”（可能是上一个变体的图集，也可能是默认相册）。
      // 让我们尝试修改为：只有当找到有效图集时才 applyGallery，否则什么都不做（return），或者根据之前的需求 restore。
      // 但用户明确说了“就不要更新轮播”。我先尝试“什么都不做”的策略。
      
      if (group && Array.isArray(group.gallery) && group.gallery.length > 0) {
        // Ensure we have the original state saved before any DOM mutations
        this.captureOriginal();
        console.log('VariantGallery: apply gallery', group.gallery.length);
        this.applyGallery(group.gallery, variant);
      } else {
        // Do nothing as per user request "如果没有在window.variantGroups 中找到对应的图集 就不要更新轮播"
        // However, we might want to restore if we are currently showing a custom gallery?
        // Let's stick to strict "do not update".
        console.log('VariantGallery: no gallery found, skipping update');
      }
    },

    applyGallery(urls, variant) {
      // Desktop: replace grid items using .product-images__slide structure to match CSS
      if (this.grid && isDesktop()) {
        // 生成与主题一致的网格结构：product-grid-item -> product-grid-media -> product-grid-media-image
        const items = urls
          .map((url, i) => {
            const eager = i === 0 ? 'eager' : 'lazy';
            const mediaId = `variant-${variant.option1}-${i}`;
            return `
              <div class="product-grid-item" data-media-id="${mediaId}">
                <div class="product-grid-media is-active" data-media-id="${mediaId}">
                  <div class="product-grid-media-image" style="--aspect-ratio: 1;">
                    <img src="${url}" alt="${variant.option1} ${i + 1}" loading="${eager}" />
                  </div>
                </div>
              </div>
            `;
          })
          .join('');
        this.grid.innerHTML = items;
        this.refreshGridPreview();
      }

      // Mobile: rebuild the slider slides with the gallery images and reinit
      if (this.slider && !isDesktop()) {
        // Destroy current Flickity instance if it exists to cleanly remove slides
        if (this.slider.flkty) {
            this.slider.flkty.destroy();
        }
        
        // Clear existing slides
        this.slider.querySelectorAll('.product-images__slide').forEach((el) => el.remove());

        urls.forEach((url, i) => {
          const mediaId = `variant-${variant.option1}-${i}`;
          const slide = document.createElement('div');
          slide.className = 'product-images__slide is-active';
          slide.setAttribute('data-media-id', mediaId);

          const media = document.createElement('div');
          media.className = 'product-single__media product-single__media-image aspect-ratio aspect-ratio--adapt';
          media.style.setProperty('--padding-bottom', '100.0%');
          media.style.setProperty('height', '0');
          media.style.setProperty('padding-bottom', '100%');
          media.style.setProperty('position', 'relative');

          const img = document.createElement('img');
          // Add lazyload classes to match original structure and trigger lazysizes if needed
          img.className = 'lazyload'; 
          // Reset absolute positioning overrides since we want to match original structure more closely
          // But keep width/height 100% if the container is aspect-ratio controlled
          img.style.setProperty('position', 'absolute');
          img.style.setProperty('top', '0');
          img.style.setProperty('left', '0');
          img.style.setProperty('width', '100%');
          img.style.setProperty('height', '100%');
          img.style.setProperty('object-fit', 'cover');
          
          // Set src to data-src for lazyload to pick up, or just src if we want immediate
          // The original has src="...width=20" and data-srcset
          // We can just set src directly if we don't want to rely on lazyload script re-running
          img.src = url; 
          
          // Important: Add data-sizes="auto" like original
          img.setAttribute('data-sizes', 'auto');
          img.setAttribute('width', '1024'); // specific width/height might help layout
          img.setAttribute('height', '1024');
          img.alt = `${variant.option1} ${i + 1}`;
          img.loading = i === 0 ? 'eager' : 'lazy';
          img.setAttribute('data-media-id', mediaId);

          if (this.lightboxEnabled) {
            const zoom = document.createElement('a');
            zoom.href = url;
            zoom.className = 'product-single__media-zoom';
            zoom.setAttribute('target', '_blank');
            zoom.setAttribute('tabindex', '-1');
            zoom.setAttribute('title', 'Zoom');
            zoom.dataset.index = String(i);
            zoom.dataset.msrc = this.toWidthUrl(url, 20);
            zoom.dataset.w = '1024';
            zoom.dataset.h = '1024';
            zoom.innerHTML = this.getZoomSvg();
            img.addEventListener('load', () => {
              if (img.naturalWidth) zoom.dataset.w = String(img.naturalWidth);
              if (img.naturalHeight) zoom.dataset.h = String(img.naturalHeight);
            });
            media.appendChild(zoom);
          }

          media.appendChild(img);
          slide.appendChild(media);
          this.slider.appendChild(slide);
        });

        try {
          if (typeof this.slider.setOptions === 'function') this.slider.setOptions();
          if (typeof this.slider.init === 'function') {
            requestAnimationFrame(() => {
              this.slider.init();
              if (this.slider.flkty) this.slider.flkty.resize();
            });
          }
        } catch (err) {
          console.warn('VariantGallery: slider init failed', err);
        }
      }
    },

    restore() {
      // Restore desktop grid：优先使用模板中的默认相册
      if (this.grid && isDesktop()) {
        const tplId = this.grid.dataset && this.grid.dataset.defaultGridTemplate;
        const tpl = tplId ? this.root.querySelector(`#${tplId}`) : null;
        if (tpl) {
          this.grid.innerHTML = tpl.innerHTML;
        } else if (typeof this.originalGridHTML === 'string') {
          this.grid.innerHTML = this.originalGridHTML;
        }
        this.refreshGridPreview();
      }

      // Restore mobile slider
      if (this.slider && typeof this.originalSliderHTML === 'string' && !isDesktop()) {
         if (this.slider.flkty) {
            this.slider.flkty.destroy();
        }
        this.slider.innerHTML = this.originalSliderHTML;
        
         if (this.slider.init) {
             this.slider.setOptions();
             requestAnimationFrame(() => {
                this.slider.init();
                if (this.slider.flkty) {
                     this.slider.flkty.resize();
                 }
             });
         }
         if (typeof this.slider.setupProductGallery === 'function') {
          this.slider.setupProductGallery();
         }
      }
    },

    toWidthUrl(url, width) {
      try {
        const u = new URL(url, window.location.origin);
        u.searchParams.set('width', String(width));
        return u.toString();
      } catch (e) {
        return url;
      }
    },

    getZoomSvg() {
      return '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.125 3.75H16.25V6.875" stroke="var(--color-body)" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.875 8.125L16.25 3.75" stroke="var(--color-body)" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/><path d="M6.875 16.25H3.75V13.125" stroke="var(--color-body)" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.125 11.875L3.75 16.25" stroke="var(--color-body)" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/><path d="M16.25 13.125V16.25H13.125" stroke="var(--color-body)" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/><path d="M11.875 11.875L16.25 16.25" stroke="var(--color-body)" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/><path d="M3.75 6.875V3.75H6.875" stroke="var(--color-body)" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/><path d="M8.125 8.125L3.75 3.75" stroke="var(--color-body)" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    },

    initGridPreview() {
      if (!this.grid) return;
      const modalId = this.grid.dataset && this.grid.dataset.previewModal;
      if (!modalId) return;
      this.previewModal = document.getElementById(modalId);
      if (!this.previewModal) return;

      this.previewThumbs = this.previewModal.querySelector('.product-grid-preview-modal__thumbs');
      this.previewThumbsList = this.previewModal.querySelector('.product-grid-preview-modal__thumbs-list');
      this.previewThumbsTrack = this.previewModal.querySelector('.product-grid-preview-modal__thumbs-track');
      this.previewThumbUp = this.previewModal.querySelector('.product-grid-preview-modal__thumb-nav--up');
      this.previewThumbDown = this.previewModal.querySelector('.product-grid-preview-modal__thumb-nav--down');
      this.previewImage = this.previewModal.querySelector('.product-grid-preview-modal__image');
      if (!this.previewThumbs || !this.previewImage) return;

      this.previewItems = [];
      this.previewIndex = 0;
      this.previewThumbOffset = 0;
      this.previewThumbStep = 0;
      this.previewThumbVisibleCount = 0;

      this.grid.addEventListener('click', (e) => {
        if (!isDesktop()) return;
        const zoomEl = e.target.closest('.product-grid-media-zoom, .product-single__media-zoom');
        if (zoomEl) return;

        const imgEl = e.target.closest('.product-images-grid img');
        if (!imgEl || !this.grid.contains(imgEl)) return;

        const images = this.getGridImageElements();
        const clickedIndex = images.indexOf(imgEl);
        if (clickedIndex < 0) return;

        e.preventDefault();
        const items = images
          .map((img) => this.resolveImageUrl(img))
          .filter(Boolean)
          .map((url) => ({ full: url, thumb: this.toThumbUrl(url) }));

        if (items.length === 0) return;

        this.previewItems = items;
        this.previewIndex = Math.min(Math.max(clickedIndex, 0), items.length - 1);
        this.renderGridPreview();
        this.previewModal.show(imgEl);
        requestAnimationFrame(() => {
          this.ensureGridPreviewThumbVisible(this.previewIndex);
          this.scheduleGridPreviewThumbNavUpdate();
        });
      });

      this.previewThumbs.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-index]');
        if (!btn) return;
        const i = parseInt(btn.dataset.index, 10);
        if (Number.isNaN(i)) return;
        this.setGridPreviewIndex(i);
      });

      if (this.previewThumbUp) {
        this.previewThumbUp.addEventListener('click', () => this.stepGridPreviewThumbs(-1));
      }
      if (this.previewThumbDown) {
        this.previewThumbDown.addEventListener('click', () => this.stepGridPreviewThumbs(1));
      }
      window.addEventListener('resize', () => this.scheduleGridPreviewThumbNavUpdate());

      this.previewModal.addEventListener('keydown', (e) => {
        if (!this.previewModal.hasAttribute('open')) return;
        if (e.code === 'ArrowLeft') {
          e.preventDefault();
          this.setGridPreviewIndex(this.previewIndex - 1);
        } else if (e.code === 'ArrowRight') {
          e.preventDefault();
          this.setGridPreviewIndex(this.previewIndex + 1);
        }
      });

      this.gridObserver = new MutationObserver(() => this.refreshGridPreview());
      this.gridObserver.observe(this.grid, { childList: true, subtree: true });
    },

    refreshGridPreview() {
      if (!this.previewModal || !this.previewModal.hasAttribute('open')) return;
      const currentUrl = this.previewItems[this.previewIndex] && this.previewItems[this.previewIndex].full;
      const images = this.getGridImageElements();
      const items = images
        .map((img) => this.resolveImageUrl(img))
        .filter(Boolean)
        .map((url) => ({ full: url, thumb: this.toThumbUrl(url) }));
      if (items.length === 0) return;

      let nextIndex = 0;
      if (currentUrl) {
        const matchedIndex = items.findIndex((it) => it.full === currentUrl);
        if (matchedIndex >= 0) nextIndex = matchedIndex;
      }
      this.previewItems = items;
      this.previewIndex = Math.min(Math.max(nextIndex, 0), items.length - 1);
      this.renderGridPreview();
    },

    getGridImageElements() {
      if (!this.grid) return [];
      const nodes = Array.from(this.grid.querySelectorAll('.product-grid-media-image img, .product-grid-media-image picture img, .product-single__media img'));
      const unique = [];
      const seen = new Set();
      nodes.forEach((img) => {
        const key = img.getAttribute('data-srcset') || img.getAttribute('srcset') || img.currentSrc || img.src || '';
        if (!key) return;
        if (seen.has(img)) return;
        seen.add(img);
        unique.push(img);
      });
      return unique;
    },

    resolveImageUrl(img) {
      if (!img) return '';
      const srcset = img.getAttribute('data-srcset') || img.getAttribute('srcset');
      const bestFromSrcset = this.pickLargestFromSrcset(srcset);
      return bestFromSrcset || img.currentSrc || img.src || '';
    },

    pickLargestFromSrcset(srcset) {
      if (!srcset || typeof srcset !== 'string') return '';
      const entries = srcset
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      let bestUrl = '';
      let bestW = 0;
      entries.forEach((entry) => {
        const parts = entry.split(/\s+/).filter(Boolean);
        const url = parts[0];
        const desc = parts[1] || '';
        let w = 0;
        if (desc.endsWith('w')) {
          const n = parseInt(desc.replace('w', ''), 10);
          if (!Number.isNaN(n)) w = n;
        }
        if (!bestUrl || w >= bestW) {
          bestUrl = url;
          bestW = w;
        }
      });
      return bestUrl;
    },

    toThumbUrl(url) {
      try {
        const u = new URL(url, window.location.origin);
        u.searchParams.set('width', '120');
        return u.toString();
      } catch (e) {
        return url;
      }
    },

    renderGridPreview() {
      const track = this.getGridPreviewThumbTrack();
      if (!track) return;
      track.innerHTML = '';
      this.previewItems.forEach((item, i) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'product-grid-preview-modal__thumb' + (i === this.previewIndex ? ' is-active' : '');
        btn.dataset.index = String(i);

        const img = document.createElement('img');
        img.src = item.thumb;
        img.alt = '';
        img.loading = 'lazy';
        img.width = 90;
        img.height = 90;
        btn.appendChild(img);
        track.appendChild(btn);
      });
      this.previewThumbOffset = 0;
      this.applyGridPreviewThumbTransform();
      this.setGridPreviewIndex(this.previewIndex);
    },

    setGridPreviewIndex(i) {
      if (!this.previewItems || this.previewItems.length === 0) return;
      const next = Math.min(Math.max(i, 0), this.previewItems.length - 1);
      this.previewIndex = next;
      const item = this.previewItems[next];
      if (item && item.full) this.previewImage.src = item.full;

      const track = this.getGridPreviewThumbTrack();
      if (!track) return;
      const btns = Array.from(track.querySelectorAll('button[data-index]'));
      btns.forEach((b) => {
        const idx = parseInt(b.dataset.index, 10);
        b.classList.toggle('is-active', idx === next);
      });
      this.ensureGridPreviewThumbVisible(next);
      this.scheduleGridPreviewThumbNavUpdate();
    },

    getGridPreviewThumbViewport() {
      return this.previewThumbsList;
    },

    getGridPreviewThumbTrack() {
      return this.previewThumbsTrack || this.previewThumbsList || this.previewThumbs;
    },

    updateGridPreviewThumbMetrics() {
      const viewport = this.getGridPreviewThumbViewport();
      const track = this.getGridPreviewThumbTrack();
      if (!viewport || !track) return;

      const first = track.querySelector('.product-grid-preview-modal__thumb');
      const style = window.getComputedStyle(track);
      const gap = parseFloat(style.rowGap || style.gap || '0') || 0;
      const thumbH = first ? first.getBoundingClientRect().height : 0;
      this.previewThumbStep = (thumbH + gap) || 0;
      const innerH = this.getGridPreviewThumbViewportInnerHeight(viewport);
      const visible = this.previewThumbStep ? Math.floor(innerH / this.previewThumbStep) : 0;
      this.previewThumbVisibleCount = Math.max(visible, 1);
    },

    getGridPreviewThumbViewportInnerHeight(viewport) {
      const style = window.getComputedStyle(viewport);
      const padTop = parseFloat(style.paddingTop || '0') || 0;
      const padBottom = parseFloat(style.paddingBottom || '0') || 0;
      return Math.max(0, viewport.clientHeight - padTop - padBottom);
    },

    applyGridPreviewThumbTransform() {
      const track = this.getGridPreviewThumbTrack();
      if (!track) return;
      track.style.transform = `translate3d(0, ${-this.previewThumbOffset}px, 0)`;
    },

    setGridPreviewThumbOffsetPx(px) {
      const viewport = this.getGridPreviewThumbViewport();
      const track = this.getGridPreviewThumbTrack();
      if (!viewport || !track) return;

      const innerH = this.getGridPreviewThumbViewportInnerHeight(viewport);
      const max = Math.max(0, track.scrollHeight - innerH);
      this.previewThumbOffset = Math.min(Math.max(px, 0), max);
      this.applyGridPreviewThumbTransform();
    },

    setGridPreviewThumbOffsetIndex(idx) {
      this.updateGridPreviewThumbMetrics();
      if (!this.previewThumbStep) return;
      this.setGridPreviewThumbOffsetPx(idx * this.previewThumbStep);
    },

    stepGridPreviewThumbs(direction) {
      this.updateGridPreviewThumbMetrics();
      if (!this.previewThumbStep) return;
      this.setGridPreviewThumbOffsetPx(this.previewThumbOffset + direction * this.previewThumbStep);
      this.scheduleGridPreviewThumbNavUpdate();
    },

    ensureGridPreviewThumbVisible(index) {
      this.updateGridPreviewThumbMetrics();
      if (!this.previewThumbStep) return;
      const viewport = this.getGridPreviewThumbViewport();
      if (!viewport) return;
      const innerH = this.getGridPreviewThumbViewportInnerHeight(viewport);
      const topY = index * this.previewThumbStep;
      const bottomY = topY + this.previewThumbStep;
      const viewTop = this.previewThumbOffset;
      const viewBottom = viewTop + innerH;

      if (topY < viewTop) {
        this.setGridPreviewThumbOffsetPx(topY);
      } else if (bottomY > viewBottom) {
        this.setGridPreviewThumbOffsetPx(bottomY - innerH);
      }
    },

    scheduleGridPreviewThumbNavUpdate() {
      if (this.thumbNavRaf) cancelAnimationFrame(this.thumbNavRaf);
      this.thumbNavRaf = requestAnimationFrame(() => this.updateGridPreviewThumbNav());
    },

    updateGridPreviewThumbNav() {
      const viewport = this.getGridPreviewThumbViewport();
      const track = this.getGridPreviewThumbTrack();
      if (!viewport || !track || !this.previewThumbUp || !this.previewThumbDown) return;

      const innerH = this.getGridPreviewThumbViewportInnerHeight(viewport);
      const canScroll = track.scrollHeight - innerH > 1;
      this.previewThumbUp.hidden = !canScroll;
      this.previewThumbDown.hidden = !canScroll;

      if (!canScroll) {
        this.previewThumbUp.disabled = true;
        this.previewThumbDown.disabled = true;
        return;
      }

      this.updateGridPreviewThumbMetrics();
      this.ensureGridPreviewThumbVisible(this.previewIndex);
      const max = Math.max(0, track.scrollHeight - innerH);
      const top = this.previewThumbOffset;
      this.previewThumbUp.disabled = top <= 1;
      this.previewThumbDown.disabled = top >= max - 1;
    },
  };

  const initializedRoots = new WeakSet();
  const initVariantGalleries = (scope = document) => {
    const roots = [];
    if (scope.matches?.('.thb-product-detail')) roots.push(scope);
    scope.querySelectorAll?.('.thb-product-detail').forEach((root) => roots.push(root));

    roots.forEach((root) => {
      if (initializedRoots.has(root)) return;
      initializedRoots.add(root);
      Object.create(VariantGallery).init(root);
    });
  };

  document.addEventListener('DOMContentLoaded', () => initVariantGalleries());
  document.addEventListener('quick-view:open', () => {
    const drawerContent = document.querySelector('#Product-Drawer.active #Product-Drawer-Content');
    if (drawerContent) initVariantGalleries(drawerContent);
  });
})();
